// packages/auth/src/AuthContext.ts

import { createContext } from 'react';

import type {
  ConsultantRegistrationStatus,
  ConsultantType,
  OnboardingCompletion,
  SchemaType,
  User,
} from '@alamo/types';

export const AUTH_PERMISSIONS_STALE_EVENT = 'alamo:auth-permissions-stale';

export interface AuthContextValue {
  /** Currently authenticated user. Null if not logged in. */
  user: User | null;
  /** Convenience boolean: user !== null */
  isAuthenticated: boolean;
  /** True while initial auth check is in progress (checking stored refresh token) */
  isLoading: boolean;
  /** User's permissions array (from GET /auth/me response) */
  permissions: string[];
  /** User's role name */
  roleName: string | null;
  /** User's schema type (kept for compatibility, always null in Alamo single-tenant) */
  schemaType: SchemaType | null;
  /**
   * Consultant's registration status from GET /auth/me. Null when the user
   * has no consultant_profile (admin, gerente). `active` means fully approved
   * and onboarded; every other non-null value is a pre-approved / pending
   * state the AppLayout surfaces via the PreApprovalBanner + the
   * preApprovalOnly sidebar items (consultant keeps operating normally).
   */
  registrationStatus: ConsultantRegistrationStatus | null;
  /**
   * True when the user's registration is settled: either a non-consultant
   * role (no consultant_profile, so registrationStatus is null) OR a
   * consultant whose registrationStatus has reached `active`. Every other
   * consultant state shows the pre-approval banner/sidebar extras inside the
   * regular AppLayout chrome.
   */
  isApproved: boolean;
  /**
   * Consultant type ('clt' | 'pf' | 'pj'), or null when the user has no
   * consultant_profile. Use isMlmEligible helper from usePermissions() to
   * gate MLM features (team, commissions, ranking, referrals).
   */
  consultantType: ConsultantType | null;
  /**
   * Whether the user receives commission, sourced from /auth/me
   * (`commission_eligible`). True for pf/pj with the flag on; false for CLT
   * and employer-absorbed consultants; null when the user has no
   * consultant_profile (admin, gerente). Drives the sidebar commission item
   * (Phase 32) — hidden when this is explicitly false.
   */
  commissionEligible: boolean | null;
  /**
   * Slug ativo do link público de auto-cotação do consultor. Vindo de
   * `public_quote_link_configs.slug` via /auth/me. Null para users sem
   * consultant_profile ou consultor sem link ativo configurado.
   * Consumido em QuotesListPage para o botão "Copiar link de auto-cotação".
   */
  publicLinkSlug: string | null;
  /** Server-authoritative completion state used to hide quotation entry points. */
  onboardingCompletion: OnboardingCompletion | null;

  /** Authenticate with email/password. Session always lasts the absolute 12h window (B1). */
  login: (email: string, password: string) => Promise<void>;
  /** Complete 2FA verification */
  complete2FA: (tempToken: string, code: string) => Promise<void>;
  /** Clear tokens and redirect to login */
  logout: () => Promise<void>;
  /** Manually refresh user data from backend */
  refreshUser: () => Promise<void>;
  /**
   * Hydrate the session with pre-issued tokens (e.g. from first-access
   * set-password auto-login). Persists the refresh token and calls /auth/me
   * so registrationStatus, permissions and consultantType reflect backend
   * state without forcing the user to re-authenticate manually.
   */
  hydrateFromTokens: (tokens: { accessToken: string; refreshToken: string }) => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);
